import { useState } from "react";
import { useCreateShortLinkMutation } from "@/hooks/ReactQuery/ShortLinkReactQuery/ShortLinkReactQuery";
import { useUserPageContextValueHook } from "@/contexts/UserPageContextProvider";
import { CHANNELS, CUSTOM_MEDIUM, ChannelIcon, channelTargetUrl, parseChannelLink } from "../../../channelLinks";
import { copyShortUrl } from "../utils";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { ChartIcon, CopyIcon, QrIcon } from "./Icons";

const ICON_BUTTON = "flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700";
const MAX_NAME = 30;

/**
 * 채널별 내 페이지 링크: 채널을 고르면 내 페이지 주소에 UTM 을 붙인 단축 URL 을 한 번에 만든다.
 * 이미 만든 채널(같은 캠페인)은 다시 만들지 않고, 만든 링크는 채널별로 복사·QR·통계를 바로 쓸 수 있게 모아 보여 준다.
 */
export function FgChannelLinks({ items, isLoading, onOpenQr, onOpenStats }) {
    const domainName = useUserPageContextValueHook()?.userPage?.domainName;
    const createShortLinkMutation = useCreateShortLinkMutation();

    const [selected, setSelected] = useState([]);
    const [customOn, setCustomOn] = useState(false);
    const [customName, setCustomName] = useState('');
    const [campaignOpen, setCampaignOpen] = useState(false);
    const [campaign, setCampaign] = useState('');
    const [creating, setCreating] = useState(false);

    const campaignValue = campaignOpen ? campaign.trim() : '';
    const customValue = customName.trim();

    // 내 페이지 채널 링크 (목록에 불러온 단축 URL 기준)
    const channelLinks = (items || [])
        .map(item => ({ item, link: parseChannelLink(item.url, domainName) }))
        .filter(r => r.link);
    const exists = (source, medium) => channelLinks.some(({ link }) => link.source === source && link.medium === medium && (link.campaign || '') === campaignValue);

    const targets = [
        ...CHANNELS.filter(c => selected.includes(c.key) && !exists(c.source, c.medium)),
        ...(customOn && customValue && !exists(customValue, CUSTOM_MEDIUM) ? [{ key: 'custom', label: customValue, source: customValue, medium: CUSTOM_MEDIUM }] : []),
    ];

    const toggle = (key) => setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

    const handleCreate = async () => {
        setCreating(true);
        try {
            // 하나가 실패해도(토스트로 안내) 나머지는 계속 만든다
            let made = 0;
            for (const channel of targets) {
                made += await createShortLinkMutation.mutateAsync({
                    body: {
                        url: channelTargetUrl(domainName, channel, campaignValue),
                        title: [`${channel.label} 채널`, campaignValue].filter(Boolean).join(' · ').slice(0, 50),
                        openInApp: false,
                    },
                }).then(() => 1, () => 0);
            }
            if (made) {
                customToast.success(`채널 링크 ${made}개를 만들었어요. 아래에서 복사해 각 채널에 붙여 넣으세요.`);
            }
            setSelected([]);
            setCustomOn(false);
            setCustomName('');
        } finally {
            setCreating(false);
        }
    };

    if (!domainName) {
        return null;
    }

    return (
        <section id="channel-links" className="rounded-[15px] border border-gray-100 shadow-[var(--defaultBoxShadow)] bg-white p-5 min-[993px]:p-7 flex flex-col gap-5">
            <div>
                <h2 className="flex items-center gap-2 text-xl font-bold">
                    채널별 내 페이지 링크
                    <span className="rounded-full bg-[var(--mainColor200)] px-2 py-0.5 text-[11px] font-bold text-[var(--mainColor)]">추천</span>
                </h2>
                <p className="mt-1 text-sm text-gray-500 break-keep">
                    유튜브·인스타·카톡에 서로 다른 링크를 붙여 두면, <b className="text-gray-700">어느 채널에서 온 사람이 링크를 많이 누르는지</b> 유입분석에서 비교할 수 있어요.
                </p>
            </div>

            <div className="flex flex-col gap-3">
                <p className="text-sm font-bold">1. 링크를 올릴 채널을 고르세요</p>
                <div className="flex flex-wrap gap-2" role="group" aria-label="채널">
                    {CHANNELS.map(c => {
                        const made = exists(c.source, c.medium);
                        const on = selected.includes(c.key) && !made;
                        return (
                            <button
                                key={c.key}
                                type="button"
                                disabled={made || creating}
                                aria-pressed={on}
                                onClick={() => toggle(c.key)}
                                className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition ${made ? 'cursor-default bg-gray-50 text-gray-400 ring-1 ring-gray-200' : on ? 'bg-[var(--mainColor100)] text-gray-900 ring-2 ring-[var(--mainColor)]' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'}`}
                            >
                                <ChannelIcon channel={c} className={`h-[18px] w-[18px] ${made ? 'opacity-40 grayscale' : ''}`} />
                                {c.label}
                                {on && <span className="text-[var(--mainColor)]" aria-hidden="true">✓</span>}
                                {made && <span className="text-[11px] font-medium">· 만들어짐</span>}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        disabled={creating}
                        aria-pressed={customOn}
                        onClick={() => setCustomOn(!customOn)}
                        className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition ${customOn ? 'bg-[var(--mainColor100)] text-gray-900 ring-2 ring-[var(--mainColor)]' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'}`}
                    >
                        ＋ 직접 입력
                    </button>
                </div>
                {customOn &&
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-gray-600">채널 이름 <span className="font-normal text-gray-400">utm_source 로 들어가요 (예: band, facebook, 전단지)</span></span>
                        <input
                            autoFocus
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value.replace(/\s/g, '_'))}
                            maxLength={MAX_NAME}
                            placeholder="채널 이름"
                            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[var(--mainColor)] min-[600px]:max-w-[280px]"
                        />
                        {customValue && exists(customValue, CUSTOM_MEDIUM) && <span className="text-xs text-gray-500">이미 만든 채널이에요.</span>}
                    </label>
                }

                <div>
                    <button type="button" onClick={() => setCampaignOpen(!campaignOpen)} aria-expanded={campaignOpen} className="text-xs font-semibold text-gray-500 underline-offset-2 hover:underline">
                        {campaignOpen ? '－ 캠페인 이름 빼기' : '＋ 캠페인 이름 붙이기 (선택)'}
                    </button>
                    {campaignOpen &&
                        <label className="mt-2 flex flex-col gap-1">
                            <span className="text-xs text-gray-500 break-keep">이벤트·시즌별로 나눠 보고 싶을 때만 쓰세요. 같은 채널이라도 캠페인이 다르면 새 링크를 만들어요.</span>
                            <input
                                value={campaign}
                                onChange={(e) => setCampaign(e.target.value.replace(/\s/g, '_'))}
                                maxLength={MAX_NAME}
                                placeholder="예: 10월_가을세일"
                                className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[var(--mainColor)] min-[600px]:max-w-[280px]"
                            />
                        </label>
                    }
                </div>

                <button
                    type="button"
                    disabled={!targets.length || creating}
                    onClick={handleCreate}
                    className="self-start rounded-xl bg-gray-900 px-5 py-3 text-[15px] font-bold text-white hover:bg-gray-800 disabled:bg-gray-300"
                >
                    {creating ? '만드는 중...' : targets.length ? `링크 ${targets.length}개 만들기` : '채널을 골라 주세요'}
                </button>
            </div>

            <div className="flex flex-col gap-2">
                <p className="text-sm font-bold">2. 각 채널에 링크를 붙여 넣으세요</p>
                {isLoading && <div className="h-16 animate-pulse rounded-xl bg-gray-100" aria-busy="true" />}
                {!isLoading && channelLinks.length === 0 &&
                    <p className="rounded-xl bg-gray-50 px-4 py-5 text-center text-xs text-gray-500">아직 채널 링크가 없어요. 위에서 채널을 골라 만들어 보세요.</p>
                }
                {channelLinks.length > 0 &&
                    <ul className="flex flex-col divide-y divide-gray-100 rounded-xl border border-gray-100">
                        {channelLinks.map(({ item, link }) => (
                            <li key={item.id} className="flex flex-col gap-2 px-4 py-3 min-[600px]:flex-row min-[600px]:items-center">
                                <div className="min-w-0 flex-1">
                                    <p className="flex flex-wrap items-center gap-1.5 text-sm font-bold">
                                        <ChannelIcon channel={link.channel} />
                                        {link.channel?.label || link.source}
                                        {link.campaign && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">{link.campaign}</span>}
                                    </p>
                                    <button type="button" onClick={() => copyShortUrl(item.shortUrl)} title="클릭해서 복사" className="text-left text-[15px] font-semibold text-gray-800 break-all hover:text-[var(--mainColor)]">
                                        {item.shortUrl.replace(/^https?:\/\//, '')}
                                    </button>
                                    {link.channel?.guide && <p className="text-xs text-gray-500">{link.channel.guide}</p>}
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <button type="button" onClick={() => onOpenStats(item)} title="클릭 통계" className="mr-1 rounded-lg px-2 py-1 text-right hover:bg-gray-50">
                                        <span className="block text-base font-bold leading-tight">{item.clicks.toLocaleString()}</span>
                                        <span className="block text-[11px] text-gray-500">클릭</span>
                                    </button>
                                    <button type="button" onClick={() => copyShortUrl(item.shortUrl)} className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800"><CopyIcon /> 복사</button>
                                    <button type="button" onClick={() => onOpenQr(item)} className={ICON_BUTTON} aria-label="QR 코드" title="QR 코드"><QrIcon /></button>
                                    <button type="button" onClick={() => onOpenStats(item)} className={ICON_BUTTON} aria-label="클릭 통계" title="클릭 통계"><ChartIcon /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                }
                <p className="text-xs text-gray-500 break-keep">
                    링크가 눌린 뒤 방문자가 내 페이지에서 무엇을 눌렀는지는 <b className="text-gray-700">유입분석 › 캠페인 (UTM)</b> 에서 채널별로 비교할 수 있어요 (Pro).
                </p>
            </div>
        </section>
    );
}
